import { Action, createReducer, on } from '@ngrx/store';
import * as BoardStateActions from './board-state.actions';
import { DatasetsDto, DatasetReferenceTable, DataTableSchema, Dataset } from 'src/app/services/models/datasets.models';
import { MetricDto } from 'src/app/services/models/metric.models';
import { UserBoard } from 'src/app/services/models/user-board.model';
import { Filter } from 'src/app/services/models/filter.model';
import { FilterBar } from 'src/app/services/models/filter-bar.model';

export const boardStateFeatureKey = 'boardState';

export interface BoardState
{

  colors: string[];
  datasets: Map<number, Dataset>;
  userBoards: UserBoard[];
  filters: Filter[];
  filterBars: FilterBar[];
  metrics: Map<number, MetricDto>;
  error: any;
}


export const initialState: BoardState = {
  colors: null,
  datasets: new Map<number, Dataset>(),
  userBoards: [],
  filters: [],
  filterBars: [],
  metrics: new Map<number, MetricDto>(),
  error: null
};

export const boardStateReducer = createReducer(
  initialState,

  on(BoardStateActions.loadBoardColorsSuccess, (state, action) => {
    return { ...state,
      colors: action.colors,
      error: null
    };
  }),
  on(BoardStateActions.loadBoardColorsFailure, (state, action) => {
    return { ...state,
      colors: null,
      error: action.error
    };
  }),
  ///DATASETS
  on(BoardStateActions.loadDatasetsSuccess, (state, action) => {
    return {...state,
      datasets: action.datasets,
      error: null
    };
  }),
  on(BoardStateActions.loadDatasetsFailure, (state, action) => {
    return { ...state,
      datasets: null,
      error: action.error
    };
  }),
  on(BoardStateActions.updateDatasetSuccess, (state, action) => {
    let updatedDatasets = new Map<number, Dataset>(state.datasets);
    updatedDatasets.set(action.dataset.datasetReferenceTable.datasetId, action.dataset);
    return {
      ...state,
      datasets: updatedDatasets
    };
  }),
  on(BoardStateActions.deleteDatasetSuccess, (state, action) => {
    let updatedDatasets = new Map(state.datasets);
    updatedDatasets.delete(action.datasetId);
    return {
      ...state,
      datasets: updatedDatasets
    };
  }),
  on(BoardStateActions.clearDatasets, (state, action) => {
    return {
      ...state,
      // Empty rather than null: an empty map fails the "already cached" check
      // so the next read refetches, without handing consumers a null to iterate.
      datasets: new Map<number, Dataset>()
    };
  }),
  ///USER BOARDS
  on(BoardStateActions.loadUserBoardSuccess, (state, action) => {
    return {...state,
      userBoards: action.userBoards,
      error: null
    };
  }),
  on(BoardStateActions.loadUserBoardFailure, (state, action) => {
    return { ...state,
      userBoards: null,
      error: action.error
    };
  }),
  on(BoardStateActions.updateUserBoardSuccess, (state, action) => {

    // Upon updating a baord, we get the userBoard object. Find user board in the list of userBoards and update that index with the new user board object.
    let updatedUserBoards = [...state.userBoards];
    const index = updatedUserBoards.findIndex((board) => board.userBoardId === action.userBoard.userBoardId);
    if (index !== -1) {
      updatedUserBoards[index] = action.userBoard;
    } else {
      updatedUserBoards.push(action.userBoard);
    }

    return {
      ...state,
      userBoards: updatedUserBoards
    };
  }),
  on(BoardStateActions.deleteUserBoardSuccess, (state, action) => {
    // Upon deleting a board, we get the userBoardId. Find user board in the list of userBoards and remove that item from the list

    let updatedUserBoards = [...state.userBoards];
    const index = updatedUserBoards.findIndex((board) => board.userBoardId === action.userBoardId);
    if (index !== -1) {
      updatedUserBoards.splice(index, 1);
    }

    return {
      ...state,
      userBoards: updatedUserBoards
    };
  }),
  on(BoardStateActions.clearUserBoard, (state, action) => {
    return {
      ...state,
      userBoards: null
    };
  }),
  ///Filters
  on(BoardStateActions.loadFilterSuccess, (state, action) => {
    return {...state,
      filters: action.filters,
      error: null
    };
  }),
  on(BoardStateActions.loadFilterFailure, (state, action) => {
    return { ...state,
      filters: [],
      error: action.error
    };
  }),
  on(BoardStateActions.updateFilterSuccess, (state, action) => {

    let updatedFilters = [...state.filters];
    const index = updatedFilters.findIndex((filter) => filter.filterId === action.filter.filterId);
    if (index !== -1) {
      updatedFilters[index] = action.filter;
    } else {
      updatedFilters.push(action.filter);
    }

    return {
      ...state,
      filters: updatedFilters
    };
  }),
  on(BoardStateActions.deleteFilterSuccess, (state, action) => {

    let updatedFilters = [...state.filters];
    const index = updatedFilters.findIndex((filter) => filter.filterId === action.filterId);
    if (index !== -1) {
      updatedFilters.splice(index, 1);
    }

    return {
      ...state,
      filters: updatedFilters
    };
  }),
  on(BoardStateActions.clearFilters, (state, action) => {
    return {
      ...state,
      filters: null
    };
  }),
  ///FilterBars
  on(BoardStateActions.loadFilterBarSuccess, (state, action) => {
    return {...state,
      filterBars: action.filterBars,
      error: null
    };
  }),
  on(BoardStateActions.loadFilterBarFailure, (state, action) => {
    return { ...state,
      filterBars: [],
      error: action.error
    };
  }),
  on(BoardStateActions.updateFilterBarSuccess, (state, action) => {

    let updatedFilterBars = [...state.filterBars];
    const index = updatedFilterBars.findIndex((filterBar) => filterBar.filterBarId === action.filterBar.filterBarId);
    if (index !== -1) {
      updatedFilterBars[index] = action.filterBar;
    } else {
      updatedFilterBars.push(action.filterBar);
    }

    return {
      ...state,
      filterBars: updatedFilterBars
    };
  }),
  on(BoardStateActions.deleteFilterBarSuccess, (state, action) => {

    let updatedFilterBars = [...state.filterBars];
    const index = updatedFilterBars.findIndex((filterBar) => filterBar.filterBarId === action.filterBarId);
    if (index !== -1) {
      updatedFilterBars.splice(index, 1);
    }

    return {
      ...state,
      filterBars: updatedFilterBars
    };
  }),
  on(BoardStateActions.clearFilterBars, (state, action) => {
    return {
      ...state,
      filterBars: null
    };
  }),
  ///METRICS
  on(BoardStateActions.loadMetricListSuccess, (state, action) => {
    return {...state,
      metrics: action.metrics,
      error: null
    };
  }),
  on(BoardStateActions.loadMetricListFailure, (state, action) => {
    return { ...state,
      metrics: null,
      error: action.error
    };
  }),
  on(BoardStateActions.updateMetricSuccess, (state, action) => {
    let updatedMetrics = new Map<number, MetricDto>(state.metrics);
    updatedMetrics.set(action.metric.metricId, action.metric);
    return {
      ...state,
      metrics: updatedMetrics
    };
  }),
  on(BoardStateActions.deleteMetricSuccess, (state, action) => {
    let updatedMetrics = new Map<number, MetricDto>(state.metrics);
    updatedMetrics.delete(action.metricId);
    return {
      ...state,
      metrics: updatedMetrics
    };
  }),
  on(BoardStateActions.clearMetrics, (state, action) => {
    return {
      ...state,
      metrics: null
    };
  })
);

export function reducer(state: BoardState | undefined, action: Action) {
  return boardStateReducer(state, action);
}
